import { useCallback, useEffect } from 'react';

import { atom, useRecoilState } from 'recoil';

export type EditableItems =
  | 'myQueue'
  | 'followed'
  | 'recents'
  | 'onDemands'
  | 'stations'
  | 'topics'
  | 'playlists'
  | 'downloads';

export type IEditModeData = {
  isChanged?: boolean;
  isEditMode: boolean;
  selectedItems: Array<string>;
};

export const defaultEditMode = {
  myQueue: { isChanged: false, isEditMode: false, selectedItems: [] },
  followed: { isEditMode: false, selectedItems: [] },
  onDemands: { isEditMode: false, selectedItems: [] },
  recents: { isEditMode: false, selectedItems: [] },
  stations: { isEditMode: false, selectedItems: [] },
  topics: { isEditMode: false, selectedItems: [] },
  playlists: { isEditMode: false, selectedItems: [] },
  downloads: { isEditMode: false, selectedItems: [] },
};

export const editModeAtom = atom<Record<EditableItems, IEditModeData>>({
  default: defaultEditMode,
  key: 'EditMode',
});

export type IEditModeHook = {
  isChanged: boolean | undefined;
  isEditMode: boolean;
  selectedItems: Array<string>;
  setIsChanged: (newState: boolean) => void;
  setIsEditMode: (newState: boolean) => void;
  setSelectedItems: (newSelectedItems: Array<string>) => void;
};

type IGlobalEditModeHook = {
  resetAllEditMode: () => void;
};

export function useGlobalEditMode(): IGlobalEditModeHook {
  const [, setEditModeState] = useRecoilState(editModeAtom);

  const resetAllEditMode = useCallback((): void => {
    setEditModeState(defaultEditMode);
  }, [setEditModeState]);

  return {
    resetAllEditMode,
  };
}

export default function useEditMode(
  itemName: EditableItems,
  defaultSelected?: Array<string>,
): IEditModeHook {
  const [editModeState, setEditModeState] = useRecoilState(editModeAtom);

  const changeState = useCallback(
    (data: Partial<IEditModeData>): void => {
      setEditModeState((oldIsEditModeState) => ({
        ...oldIsEditModeState,
        [itemName]: { ...oldIsEditModeState[itemName], ...data },
      }));
    },
    [itemName, setEditModeState],
  );

  useEffect(() => {
    if (defaultSelected) {
      changeState({ selectedItems: defaultSelected });
    }
  }, [changeState, defaultSelected]);

  const setIsEditMode = useCallback(
    (newState: boolean): void => {
      changeState({ isEditMode: newState, selectedItems: [] });
    },
    [changeState],
  );

  const setSelectedItems = useCallback(
    (newSelectedItems: Array<string>): void => {
      changeState({ selectedItems: newSelectedItems });
    },
    [changeState],
  );

  const setIsChanged = useCallback(
    (newState: boolean): void => {
      changeState({ isChanged: newState });
    },
    [changeState],
  );

  return {
    isChanged: editModeState[itemName].isChanged,
    isEditMode: editModeState[itemName].isEditMode,
    selectedItems: editModeState[itemName].selectedItems,
    setIsChanged,
    setIsEditMode,
    setSelectedItems,
  };
}
